/**
 * Component: Featured carousel
 * ------------------------------------------------------------------------------
 * Configured section to display a single collection in product cards.
 * - Includes enable slider option.
 *
 * @namespace featuredCarousel
 */
import {debounce, on} from '~/helpers/utils';
import breakpoints from '~/helpers/breakpoints';

import ScrollBooster from 'scrollbooster';

import SimpleBar from 'simplebar';
import 'simplebar/dist/simplebar.css';

/**
 * DOM selectors.
 */
const selectors = {
  simplebarContainer: '[js-simplebar="container"]',
  draggableContainer: 'simplebar-content-wrapper',
};

let draggableEl;
let observer;

/**
 * Create a new example object.
 * @param {object} config - Settings object defined when instantiating.
 */
export default () => {

  /**
   * DOM node selectors.
   */
  const nodeSelectors = {
    simplebarContainer: document.querySelectorAll(selectors.simplebarContainer),
  };

  /**
   * Initialise component.
   */
  function init() {
    if (window.matchMedia(`(min-width: ${breakpoints.medium})`).matches) {
      markupMutationObserver();
    }
    constructScrollbar();
    setEventListeners();
  }

  /**
   * Set event listeners.
   */
  function setEventListeners() {
    on('resize', debounce(() => handleResizeEvents(), 250));
  }

  /**
   * Either update metrics, destroy or build the ScrollBooster library.
   */
  function handleResizeEvents() {
    const draggableContainer = document.querySelectorAll(`.${selectors.draggableContainer}`);

    [...draggableContainer].forEach((container) => {
      if ((window.matchMedia(`(min-width: ${breakpoints.medium})`).matches)) {
        if (draggableEl) {
          draggableEl.updateMetrics();
        } else {
          constructDraggable(container);
        }
      } else {
        if (!draggableEl) {
          return;
        }
        draggableEl.destroy();
      }
    });
  }

  /**
   * Init custom scrollbar library (Simplebar).
   */
  function constructScrollbar() {
    [...nodeSelectors.simplebarContainer].forEach((container) => {
      new SimpleBar(container, {
        autoHide: false,
      });
    });
  }

  /**
   * Check if the Simplebar library has been initiated.
   * @param {Object} mutationsList - List of mutations from MutationObserver.
   */
  function checkSimplebarLoaded(mutationsList) {
    Object.keys(mutationsList).forEach((key) => {
      const draggableContainer = document.querySelectorAll(`.${selectors.draggableContainer}`);

      if (mutationsList[key].target.className !== selectors.draggableContainer) {
        return;
      }

      observer.disconnect();

      [...draggableContainer].forEach((container) => {
        constructDraggable(container);
      });
    });
  }

  /**
   * Initiate the MutationObserver.
   */
  function markupMutationObserver() {
    const settings = {
      attributes: true,
      childList: true,
      subtree: true,
    };

    [...nodeSelectors.simplebarContainer].forEach((container) => {
      observer = new MutationObserver(checkSimplebarLoaded);
      observer.observe(container, settings);
    });
  }


  /**
   * Makes the containare draggable.
   */
  function constructDraggable(container) {
    draggableEl = new ScrollBooster({
      viewport: container,
      bounce: false,
      scrollMode: 'native',
      direction: 'horizontal',
      friction: 0.2,
      onUpdate: (state) => {
        setDragMouse(state);
      },
    });
  }

  /**
   * Change the cursor over the dragged element.
   * @param {Object} state - state of dragging event.
   */
  function setDragMouse(state) {
    [...nodeSelectors.simplebarContainer].forEach((container) => {
      if (
        state.isMoving &&
        state.isDragging &&
        !state.borderCollision.left &&
        !state.borderCollision.right
      ) {
        container.classList.add('dragging');
      } else {
        container.classList.remove('dragging');
      }
    });
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  });
};
